<template>
  <div class="nt-page">
    <div class="container py-4">
      <div class="nt-head">
        <SectionHeader label="الإشعارات" title="مركز الإشعارات" subtitle="" />
        <button v-if="unread > 0" class="nt-markall" @click="markAll">
          <i class="pi pi-check-circle"></i> تعليم الكل كمقروء
        </button>
      </div>

      <div v-if="loading" class="nt-list">
        <div v-for="n in 5" :key="n" class="skel-row"></div>
      </div>

      <div v-else-if="!items.length" class="nt-empty">
        <i class="pi pi-bell"></i>
        <p>لا توجد إشعارات بعد.</p>
      </div>

      <div v-else class="nt-list">
        <button v-for="n in items" :key="n.id" class="nt-item" :class="{ 'nt-item--unread': !n.read_at }" @click="open(n)">
          <span class="nt-item__icon" :class="toneClass(n)"><i class="pi" :class="icon(n)"></i></span>
          <span class="nt-item__body">
            <span class="nt-item__title">{{ n.data?.title || 'إشعار' }}</span>

            <!-- New content: structured content details -->
            <template v-if="n.data?.type === 'new_content'">
              <span v-if="n.data?.content_title" class="nt-item__content-name">{{ n.data.content_title }}</span>
              <span class="nt-item__meta">
                <span v-if="n.data?.content_type" class="nt-badge">🏷️ النوع: {{ n.data.content_type }}</span>
                <span v-if="n.data?.branch_name" class="nt-meta-line">🏫 الفرع: {{ n.data.branch_name }}</span>
                <span v-if="n.data?.unit_name" class="nt-meta-line">📘 الوحدة: {{ n.data.unit_name }}</span>
                <span v-if="n.data?.lesson_name" class="nt-meta-line">📖 الدرس: {{ n.data.lesson_name }}</span>
                <span v-if="n.data?.academic_year_name" class="nt-meta-line">🎓 الصف: {{ n.data.academic_year_name }}</span>
              </span>
            </template>

            <!-- Other notifications: unchanged -->
            <span v-else class="nt-item__msg">{{ n.data?.message }}</span>

            <span class="nt-item__time">{{ date(n.created_at) }}</span>
          </span>
          <span v-if="!n.read_at" class="nt-item__dot"></span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import SectionHeader from '@/components/common/SectionHeader.vue';
import { subscriptionService } from '@/services/subscriptionService.js';
import { notificationIcon, formatDate } from '@/composables/subscriptionMeta.js';

const TONE = {
  payment_approved: 'green', subscription_activated: 'green',
  payment_rejected: 'red', subscription_expired: 'red',
  subscription_expiring: 'amber', payment_received: 'blue',
  new_content: 'purple',
};

export default {
  name: 'NotificationsCenter',
  components: { SectionHeader },
  data() { return { items: [], unread: 0, loading: true }; },
  methods: {
    date: formatDate,
    icon(n) { return notificationIcon(n.data?.type); },
    toneClass(n) { return 'nt-icon--' + (TONE[n.data?.type] || 'blue'); },
    async load() {
      this.loading = true;
      try {
        const res = await subscriptionService.notifications();
        this.items = res.data || [];
        this.unread = res.unread_count || 0;
      } catch { this.items = []; }
      finally { this.loading = false; }
    },
    async open(n) {
      // Deep-link to the most relevant page, and mark this one read.
      if (!n.read_at) {
        try { await subscriptionService.markNotificationRead(n.id); n.read_at = new Date().toISOString(); this.unread = Math.max(0, this.unread - 1); }
        catch { /* non-fatal */ }
      }
      const t = n.data?.type;
      if (t === 'new_content') {
        // Deep-link to the relevant content list (route stored on the payload).
        this.$router.push(n.data?.route || '/contents');
      } else if (t === 'payment_received' || t === 'payment_approved' || t === 'payment_rejected') {
        this.$router.push('/payment-history');
      } else {
        this.$router.push('/my-subscriptions');
      }
    },
    async markAll() {
      try {
        await subscriptionService.markNotificationsRead();
        this.items = this.items.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() }));
        this.unread = 0;
      } catch { /* non-fatal */ }
    },
  },
  mounted() { this.load(); },
};
</script>

<style scoped>
.nt-page { min-height: 60vh; padding-bottom: 5rem; }
.nt-head { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: .5rem; }
.nt-markall { background: rgba(91,95,239,.08); color: #5B5FEF; border: none; border-radius: 10px; padding: .5rem .9rem; font-weight: 700; cursor: pointer; white-space: nowrap; }
.nt-list { display: grid; gap: .6rem; }
.nt-item { display: flex; align-items: center; gap: .8rem; background: #fff; border: 1px solid rgba(91,95,239,.08); border-radius: 14px; padding: .9rem 1rem; text-align: start; cursor: pointer; position: relative; width: 100%; }
.nt-item--unread { background: rgba(91,95,239,.035); border-color: rgba(91,95,239,.18); }
.nt-item__icon { width: 42px; height: 42px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.1rem; flex-shrink: 0; }
.nt-icon--green { background: rgba(34,197,94,.12); color: #16a34a; }
.nt-icon--red { background: rgba(239,68,68,.12); color: #dc2626; }
.nt-icon--amber { background: rgba(245,158,11,.14); color: #b45309; }
.nt-icon--blue { background: rgba(91,95,239,.1); color: #5B5FEF; }
.nt-icon--purple { background: rgba(124,77,255,.12); color: #7C4DFF; }
.nt-item__body { display: flex; flex-direction: column; gap: .15rem; flex: 1; }
.nt-item__title { font-weight: 800; color: #111827; font-size: .92rem; }
.nt-item__msg { color: #6B7280; font-size: .82rem; line-height: 1.5; }
.nt-item__content-name { font-weight: 800; color: #4338ca; font-size: .9rem; line-height: 1.4; }
.nt-item__meta { display: flex; flex-direction: column; gap: .2rem; margin-top: .15rem; }
.nt-badge { align-self: flex-start; display: inline-flex; align-items: center; gap: .25rem; background: rgba(124,77,255,.1); color: #6d28d9; font-size: .75rem; font-weight: 700; padding: .12rem .55rem; border-radius: 999px; margin-bottom: .1rem; }
.nt-meta-line { color: #6B7280; font-size: .8rem; line-height: 1.55; }
.nt-item__time { color: #94a3b8; font-size: .72rem; }
.nt-item__dot { width: 9px; height: 9px; border-radius: 50%; background: #5B5FEF; flex-shrink: 0; }
.nt-empty { text-align: center; padding: 3.5rem 1rem; color: #94a3b8; }
.nt-empty .pi { font-size: 2.6rem; display: block; margin-bottom: .8rem; }
.skel-row { height: 68px; border-radius: 14px; background: #e9edf5; position: relative; overflow: hidden; }
.skel-row::after { content: ''; position: absolute; inset: 0; transform: translateX(-100%); background: linear-gradient(90deg, transparent, rgba(255,255,255,.6), transparent); animation: sh 1.3s infinite; }
@keyframes sh { 100% { transform: translateX(100%); } }
</style>
